import React, { useState, useEffect } from 'react';
import Wrapper from '../../Main/Universal/Wrapper';
import { postAPI } from '../../../app/api';
import Swal from 'sweetalert2';
import editIcon from "../../../theme/assets/images/icon/edit-icon.svg";
import deleteIcon from "../../../theme/assets/images/icon/delete-icon.svg";
import viewIcon from "../../../theme/assets/images/icon/view-icon-02.svg";

export default function ExecutiveCommittee() {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewMember, setViewMember] = useState(null);
    const [editingId, setEditingId] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        designation: '',
        batch: '',
        mobile: '',
        email: '',
        image: '',
        status: 1,
        sequence: 0
    });

    // Fetch all members
    useEffect(() => {
        fetchMembers();
    }, []);

    const fetchMembers = async () => {
        try {
            setLoading(true);
            const response = await postAPI('executive-committee/list', {});
            if (response?.status === 1) {
                setMembers(response?.data || []);
            }
        } catch (error) {
            console.error('Error fetching members:', error);
            Swal.fire('Error', 'Failed to fetch members', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle form changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle file upload
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({
                    ...prev,
                    image: reader.result
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            name: '',
            designation: '',
            batch: '',
            mobile: '',
            email: '',
            image: '',
            status: 1,
            sequence: 0
        });
        setEditingId(null);
        setShowForm(false);
    };

    // Handle submit (Create/Update)
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (!formData.name) {
                Swal.fire('Validation', 'Name is required', 'warning');
                return;
            }

            const endpoint = editingId ? 'executive-committee/update' : 'executive-committee/create';
            const payload = editingId ? { ...formData, id: editingId } : formData;

            const response = await postAPI(endpoint, payload);

            if (response?.status === 1) {
                Swal.fire('Success', response.msg, 'success');
                resetForm();
                fetchMembers();
            } else {
                Swal.fire('Error', response?.msg || 'Operation failed', 'error');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            Swal.fire('Error', 'Failed to save member', 'error');
        }
    };

    // Handle edit
    const handleEdit = (member) => {
        setFormData({
            name: member.name || '',
            designation: member.designation || '',
            batch: member.batch || '',
            mobile: member.mobile || '',
            email: member.email || '',
            image: member.image || '',
            status: member.status,
            sequence: member.sequence || 0
        });
        setEditingId(member.id);
        setShowForm(true);
    };

    // Handle view
    const handleView = (member) => {
        setViewMember(member);
        setShowViewModal(true);
    };

    // Handle delete
    const handleDelete = async (id) => {
        try {
            const result = await Swal.fire({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, delete it!'
            });

            if (result.isConfirmed) {
                const response = await postAPI('executive-committee/delete', { id });
                if (response?.status === 1) {
                    Swal.fire('Deleted!', 'Member has been deleted.', 'success');
                    fetchMembers();
                } else {
                    Swal.fire('Error', response?.msg || 'Delete failed', 'error');
                }
            }
        } catch (error) {
            console.error('Error deleting member:', error);
            Swal.fire('Error', 'Failed to delete member', 'error');
        }
    };

    // Handle status toggle
    const handleToggleStatus = async (id) => {
        try {
            const response = await postAPI('executive-committee/toggle-status', { id });
            if (response?.status === 1) {
                Swal.fire('Success', response.msg, 'success');
                fetchMembers();
            } else {
                Swal.fire('Error', response?.msg || 'Status update failed', 'error');
            }
        } catch (error) {
            console.error('Error toggling status:', error);
            Swal.fire('Error', 'Failed to update status', 'error');
        }
    };

    return (
        <Wrapper>
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h3 className="">Executive Committee Management</h3>
                            <button
                                className="btn btn-primary"
                                onClick={() => setShowForm(true)}
                            >
                                {showForm ? 'Cancel' : 'Add New Member'}
                            </button>
                        </div>

                            <div className="">
                                {/* Form Section */}
                                {/* Add/Edit Modal */}
                                {showForm && (
                                    <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                                        <div className="modal-dialog modal-lg modal-dialog-centered">
                                            <div className="modal-content">

                                                <div className="modal-header">
                                                    <h5 className="modal-title">
                                                        {editingId ? 'Edit Member' : 'Add New Member'}
                                                    </h5>
                                                    <button type="button" className="btn-close" onClick={resetForm}></button>
                                                </div>

                                                <div className="modal-body">
                                                    <form onSubmit={handleSubmit}>
                                                        <div className="row">
                                                            <div className="col-md-6 mb-3">
                                                                <label className="form-label">Name *</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    name="name"
                                                                    value={formData.name}
                                                                    onChange={handleChange}
                                                                    required
                                                                />
                                                            </div>

                                                            <div className="col-md-6 mb-3">
                                                                <label className="form-label">Designation</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    name="designation"
                                                                    value={formData.designation}
                                                                    onChange={handleChange}
                                                                />
                                                            </div>

                                                            <div className="col-md-4 mb-3">
                                                                <label className="form-label">Batch</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    name="batch"
                                                                    value={formData.batch}
                                                                    onChange={handleChange}
                                                                />
                                                            </div>

                                                            <div className="col-md-4 mb-3">
                                                                <label className="form-label">Mobile</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    name="mobile"
                                                                    value={formData.mobile}
                                                                    onChange={handleChange}
                                                                />
                                                            </div>

                                                            <div className="col-md-4 mb-3">
                                                                <label className="form-label">Email</label>
                                                                <input
                                                                    type="email"
                                                                    className="form-control"
                                                                    name="email"
                                                                    value={formData.email}
                                                                    onChange={handleChange}
                                                                />
                                                            </div>

                                                            <div className="col-md-12 mb-3">
                                                                <label className="form-label">Image</label>
                                                                <input
                                                                    type="file"
                                                                    className="form-control"
                                                                    accept="image/*"
                                                                    onChange={handleFileChange}
                                                                />
                                                                {formData.image && (
                                                                    <img
                                                                        src={formData.image}
                                                                        alt="Preview"
                                                                        className="mt-2"
                                                                        style={{ width: "150px", height: "150px", objectFit: "cover" }}
                                                                    />
                                                                )}
                                                            </div>

                                                            <div className="col-md-12 mb-3">
                                                                <label className="form-label">Status</label>
                                                                <select
                                                                    className="form-control"
                                                                    name="status"
                                                                    value={formData.status}
                                                                    onChange={handleChange}
                                                                >
                                                                    <option value={1}>Active</option>
                                                                    <option value={0}>Inactive</option>
                                                                </select>
                                                            </div>

                                                            <div className="col-md-12 mb-3">
                                                                <label className="form-label">Display Sequence</label>
                                                                <input
                                                                    type="number"
                                                                    className="form-control"
                                                                    name="sequence"
                                                                    value={formData.sequence}
                                                                    onChange={handleChange}
                                                                    min="0"
                                                                    placeholder="Enter display order (0 = first, 1 = second, etc.)"
                                                                />
                                                                <small className="text-muted">
                                                                    Lower numbers will appear first. Use 0 for first position.
                                                                </small>
                                                            </div>
                                                        </div>

                                                        <div className="modal-footer px-0">
                                                            <button type="submit" className="btn btn-primary">
                                                                {editingId ? 'Update' : 'Create'}
                                                            </button>
                                                        </div>
                                                    </form>
                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Table Section */}
                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table cus-table">
                                            <thead className="table-dark">
                                                <tr>
                                                    <th>#</th>
                                                    <th>Sequence</th>
                                                    <th>Image</th>
                                                    <th>Name</th>
                                                    <th>Designation</th>
                                                    <th>Batch</th>
                                                    <th>Mobile</th>
                                                    <th>Email</th>
                                                    <th>Status</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {members.length > 0 ? (
                                                    members.map((member, index) => (
                                                        <tr key={member.id}>
                                                            <td>{index + 1}</td>
                                                            <td>
                                                                <span className="badge bg-info text-dark">
                                                                    {member.sequence || 0}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                {member.image ? (
                                                                    <img
                                                                        src={member.image}
                                                                        alt={member.name}
                                                                        style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '5px' }}
                                                                    />
                                                                ) : (
                                                                    <span className="text-muted">No Image</span>
                                                                )}
                                                            </td>
                                                            <td>{member.name}</td>
                                                            <td>{member.designation || '-'}</td>
                                                            <td>{member.batch || '-'}</td>
                                                            <td>{member.mobile || '-'}</td>
                                                            <td>{member.email || '-'}</td>
                                                            <td>
                                                                <button
                                                                    className={`btn btn-sm ${member.status === 1
                                                                        ? 'btn-success'
                                                                        : 'btn-danger'
                                                                        }`}
                                                                    onClick={() => handleToggleStatus(member.id)}
                                                                >
                                                                    {member.status === 1 ? 'Active' : 'Inactive'}
                                                                </button>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex gap-2">
                                                                    <button
                                                                        className="btn"
                                                                        onClick={() => handleView(member)}
                                                                        title="View"
                                                                    >
                                                                        <img src={viewIcon} />
                                                                    </button>
                                                                    <button
                                                                        className="btn"
                                                                        onClick={() => handleEdit(member)}
                                                                        title="Edit"
                                                                    >
                                                                        <img
                                                                            src={editIcon}
                                                                            alt="Edit"
                                                                        />
                                                                    </button>
                                                                    <button
                                                                        className="btn"
                                                                        onClick={() => handleDelete(member.id)}
                                                                        title="Delete"
                                                                    >
                                                                        <img
                                                                            src={deleteIcon}
                                                                            alt="Delete"
                                                                        />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="10" className="text-center py-4">
                                                            No members found
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                    </div>
                </div>
            </div>

            {/* View Modal */}
            {showViewModal && viewMember && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Member Details</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowViewModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-4 text-center mb-4">
                                        {viewMember.image ? (
                                            <img
                                                src={viewMember.image}
                                                alt={viewMember.name}
                                                className="img-fluid rounded"
                                                style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <div className="bg-light p-5 rounded">
                                                <span className="text-muted">No Image</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-md-8">
                                        <table className="table table-bordered">
                                            <tbody>
                                                <tr>
                                                    <th style={{ width: '40%' }}>Name</th>
                                                    <td>{viewMember.name || '-'}</td>
                                                </tr>
                                                <tr>
                                                    <th>Designation</th>
                                                    <td>{viewMember.designation || '-'}</td>
                                                </tr>
                                                <tr>
                                                    <th>Batch</th>
                                                    <td>{viewMember.batch || '-'}</td>
                                                </tr>
                                                <tr>
                                                    <th>Mobile</th>
                                                    <td>{viewMember.mobile || '-'}</td>
                                                </tr>
                                                <tr>
                                                    <th>Email</th>
                                                    <td>{viewMember.email || '-'}</td>
                                                </tr>
                                                <tr>
                                                    <th>Status</th>
                                                    <td>
                                                        <span className={`badge ${viewMember.status === 1
                                                            ? 'bg-success'
                                                            : 'bg-danger'
                                                            }`}>
                                                            {viewMember.status === 1 ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <th>Display Sequence</th>
                                                    <td>
                                                        <span className="badge bg-info text-dark">
                                                            {viewMember.sequence || 0}
                                                        </span>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowViewModal(false)}
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => {
                                        setShowViewModal(false);
                                        handleEdit(viewMember);
                                    }}
                                >
                                    Edit Member
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Wrapper>
    );
}
